import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Building, Phone, Search, Info, Mountain, Star, Newspaper } from 'lucide-react';
import { Category, SiteSettings } from '../../types';
import { apiService } from '../../services/api';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const location = useLocation();

  useEffect(() => {
    fetchCategories();
    fetchSiteSettings();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCategories();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await apiService.getSiteSettings();
      if (response.success) {
        setSiteSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'About', href: '/about', icon: Info },
    { name: 'Properties', href: '/properties', icon: Building },
    { name: 'Exclusive', href: '/properties?exclusive=1', icon: Star },
    { name: 'Blog', href: '/blog', icon: Newspaper },
    { name: 'Contact', href: '/contact', icon: Phone },
  ];

  const isActiveLink = (href: string) => {
    const path = href.split('?')[0];
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/properties') {
      const isExclusiveLink = href.includes('exclusive=1');
      const isCurrentlyExclusive = location.search.includes('exclusive=1');
      return location.pathname.startsWith(path) && isExclusiveLink === isCurrentlyExclusive;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-primary text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-4">
              <a 
                href={`tel:${siteSettings?.contact_phone || '+919867300996'}`}
                className="flex items-center space-x-1 hover:text-secondary transition-colors duration-200"
                title={`Call us: ${siteSettings?.contact_phone || '+919867300996'}`}
              >
                <Phone className="w-3 h-3" />
                <span>{siteSettings?.contact_phone || '+919867300996'}</span>
              </a>
              <a 
                href={`mailto:${siteSettings?.contact_email || 'info@aaranhomes.com'}`}
                className="flex items-center space-x-1 hover:text-secondary transition-colors duration-200"
                title={`Email us: ${siteSettings?.contact_email || 'info@aaranhomes.com'}`}
              >
                <span>✉️</span>
                <span>{siteSettings?.contact_email || 'info@aaranhomes.com'}</span>
              </a>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <span>Welcome to {siteSettings?.site_name || 'AARAN HOMES'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/logo 1 (6).png" 
              alt="AARAN HOMES Logo" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-primary tracking-wide">AARAN HOMES</h1>
              <p className="text-xs text-gray-500 font-medium">Your One Stop Shop For Your Second Home</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isHighlight = (item as any).highlight;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isHighlight
                      ? isActiveLink(item.href)
                        ? 'text-white bg-primary border border-primary'
                        : 'text-primary border border-primary hover:bg-primary hover:text-white'
                      : isActiveLink(item.href)
                        ? 'text-primary bg-primary/10'
                        : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </Link>
              );
            })}

            {/* Categories Dropdown */}
            <div className="relative group">
              <button className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary hover:bg-gray-50 transition-colors duration-200">
                <Building className="w-4 h-4" />
                <span>Categories</span>
                <svg
                  className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-1">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.slug}`}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors duration-200"
                    >
                      <div
                        className="w-3 h-3 rounded-full mr-3"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <span>{category.name}</span>
                      {category.property_count !== undefined && (
                        <span className="ml-auto text-xs text-gray-500">
                          ({category.property_count})
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Search Button */}
            <Link
              to="/properties"
              className="bg-secondary text-primary px-6 py-2 rounded-md font-medium hover:bg-secondary/90 transition-colors duration-200 flex items-center space-x-2"
            >
              <Search className="w-4 h-4" />
              <span>Search Properties</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-primary hover:bg-gray-50 transition-colors duration-200"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-200">
            <div className="pt-4 space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                      isActiveLink(item.href)
                        ? 'text-primary bg-primary/10'
                        : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}

              {/* Mobile Categories */}
              <div className="pt-2">
                <h3 className="px-3 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  Categories
                </h3>
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.slug}`}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div
                      className="w-3 h-3 rounded-full mr-3"
                      style={{ backgroundColor: category.color }}
                    ></div>
                    <span>{category.name}</span>
                    {category.property_count !== undefined && (
                      <span className="ml-auto text-sm text-gray-500">
                        ({category.property_count})
                      </span>
                    )}
                  </Link>
                ))}
              </div>

              {/* Mobile Search Button */}
              <div className="pt-4">
                <Link
                  to="/properties"
                  onClick={() => setIsMenuOpen(false)}
                  className="w-full bg-secondary text-primary px-4 py-3 rounded-md font-medium hover:bg-secondary/90 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <Search className="w-5 h-5" />
                  <span>Search Properties</span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};